import { Heading } from "@instructure/ui";
import { t } from "i18next";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useParams } from "react-router-dom";
import ReflectionsAndActionsCard from "../../components/CourseReview/Summary/ReflectionsAndActionsCard";
import ResultsChart from "../../components/CourseReview/Summary/ResultsChart";
import { INITIAL_COURSE_REVIEW } from "../../components/CourseReview/utils";
import {
  ERROR,
  useNotification,
} from "../../components/CustomHooks/NotificationProvider";
import { ExternalService } from "../../services/ExternalService";
import { Files } from "../../services/FilesService";
import { ReviewService } from "../../services/ReviewService";

const PublicCourseReview = () => {
  const showNotification = useNotification();
  const { tenantId, shareId } = useParams();
  const [loading, setLoading] = useState(true);
  const [courseReview, setCourseReview] = useState(INITIAL_COURSE_REVIEW);
  const [courseCode, setCourseCode] = useState("");

  const results = courseReview.results.data;
  const presentationType = courseReview.results.presentation_type;
  const masteryResults = courseReview.results.mastery_results;
  const reflections = courseReview.selectedReflections;
  const reflectionsSummary = courseReview.reflectionsSummary;
  const actions = courseReview.selectedActions;
  const actionsSummary = courseReview.actionsSummary;

  useEffect(() => {
    async function fetchCourseReview() {
      try {
        setLoading(true);
        const publicShareCode = await ExternalService.getPublicShareCode(
          shareId,
          tenantId === "localhost" ? "ddl-local" : tenantId
        );
        setCourseCode(publicShareCode.payload.context.reviewable_name);

        const reviewCycleId = publicShareCode.payload.context.review_cycle_id;
        const reviewableId = publicShareCode.payload.context.reviewable_id;
        const reviewId = publicShareCode.payload.context.review_id;

        const courseReview = await Files.getPublicFileContent(
          "response.json",
          "public-reviews",
          ReviewService.getReviewFolderPath(
            reviewCycleId,
            reviewableId,
            reviewId
          ),
          tenantId === "localhost" ? "ddl-local" : tenantId
        );
        setCourseReview(JSON.parse(courseReview.content));

        setLoading(false);
      } catch {
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("courseReview.courseReview"),
          }),
          ERROR
        );
      }
    }
    fetchCourseReview();
  }, [shareId, tenantId, showNotification]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        height: "calc(-3rem + 100vh)",
      }}
    >
      <Heading as="h1" level="h2" border="bottom">
        {loading
          ? t("global.loading") + "..."
          : `${courseCode} ${t("courseReview.courseReview")}`}
      </Heading>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          flexGrow: 1,
          overflowY: "auto",
          padding: "0.75rem",
          margin: "-0.75rem",
        }}
      >
        {loading ? (
          <>
            <Skeleton height={250} />
            <div style={{ flex: 1 }}>
              <Skeleton height="100%" />
            </div>
          </>
        ) : (
          <>
            <ResultsChart
              results={results}
              presentationType={presentationType}
              masteryResults={masteryResults}
              courseCode={courseCode}
            />
            <ReflectionsAndActionsCard
              reflections={reflections}
              reflectionsSummary={reflectionsSummary}
              actions={actions}
              actionsSummary={actionsSummary}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default PublicCourseReview;
